// TODO: break this wizard into per-step components (tracked: https://app.asana.com/1/1206214889537900/project/1212886114840713/task/1214783132869822?focus=true)
import {useState, useEffect, useCallback, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {useForm, FormProvider} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import {Button} from '@common-web/components/ui/button';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {trpc} from '@common-web/lib/trpc';
import {reportError} from '@common-web/lib/errorReporting';
import {
  surfaceRfcFormErrors,
  applyRfcFieldErrors,
  rfcStepFieldPrefix,
} from './rfcFormErrors';
import {toast} from '@common-web/hooks/use-toast';
import {RfcStep, RfcStatus, RfcAttachmentType} from '@common/types/rfc';
import {STALE_TIME_1_MIN} from '@common/constants/time';
import {PasswordPrompt} from './components/PasswordPrompt';
import {RfcWizardLayout} from './components/RfcWizardLayout';
import {Building2, ArrowRight, FileCheck, FlaskConical} from 'lucide-react';
import {RfcInitialScreen} from './components/RfcInitialScreen';
import {RfcContext} from './RfcContext';
import {
  getBlankRfcFormState,
  getSampleRfcFormState,
  rfcSection125FormDraftSchema,
  rfcMecFormDraftSchema,
  rfcClientPortalAccessFormDraftSchema,
} from '@common/lib/rfcSchema';
import {RfcStepForm} from '@client/pages/rfc/forms/RfcStepForm';
import {RfcSubmittedScreen} from '@client/pages/rfc/components/RfcSubmittedScreen';
import {useInterval} from '@common-web/hooks/use-interval';

const wizardFormSchema = z.object({
  section125Form: rfcSection125FormDraftSchema,
  mecForm: rfcMecFormDraftSchema,
  clientPortalAccessForm: rfcClientPortalAccessFormDraftSchema,
});

type WizardFormData = z.infer<typeof wizardFormSchema>;

// "Use sample data" is a testing convenience — only show it locally and on dev/staging builds.
// The deploy workflows bake VITE_API_URL per environment, so it doubles as the env signal.
const SAMPLE_DATA_API_URLS = [
  'https://api-dev.cbs-infinity.com',
  'https://api-staging.cbs-infinity.com',
];
const showSampleDataButton =
  import.meta.env.DEV || SAMPLE_DATA_API_URLS.includes(import.meta.env.VITE_API_URL ?? '');

function wizardFormValuesToRfcFormData(values: WizardFormData) {
  return {
    section125FormData: values.section125Form,
    mecFormData: values.mecForm,
    clientPortalAccessFormData: values.clientPortalAccessForm,
  };
}

function getAllStepsForRfc(
  hasSection125: boolean,
  hasMec: boolean,
  hasClientPortalAccess: boolean,
  hasAch: boolean,
) {
  const steps: {
    title: string;
    subTitle?: string;
    description?: string;
    step: RfcStep;
  }[] = [];

  if (hasSection125) {
    steps.push(
      {
        title: 'Group Information',
        subTitle: hasMec ? '125 Program' : undefined,
        step: RfcStep.SECTION_125_FORM_GROUP_INFO,
        description: `Provide your company information and benefit details${hasMec ? ' for your 125 Program' : ''}`,
      },
      {
        title: 'Eligibility & Admin',
        subTitle: hasMec ? '125 Program' : undefined,
        step: RfcStep.SECTION_125_FORM_ADMIN,
        description: `Configure employee eligibility and administrative settings${hasMec ? ' for your 125 Program' : ''}`,
      },
      {
        title: 'Contacts',
        subTitle: hasMec ? '125 Program' : undefined,
        step: RfcStep.SECTION_125_FORM_CONTACTS,
        description: `Enter key contact information for your organization${hasMec ? ' related to your 125 Program' : ''}`,
      },
      {
        title: 'Payroll',
        subTitle: hasMec ? '125 Program' : undefined,
        step: RfcStep.SECTION_125_FORM_PAYROLL_INFO,
        description: `Provide payroll system details${hasMec ? ' for your 125 Program' : ''}`,
      },
    );
  }

  if (hasMec) {
    steps.push(
      {
        title: 'Group Information',
        subTitle: hasSection125 ? 'MEC Plan' : undefined,
        step: RfcStep.MEC_FORM_GROUP_INFO,
        description: `Provide your company information and benefit details${hasSection125 ? ' for your MEC plan' : ''}`,
      },
      {
        title: 'Eligibility & Admin',
        subTitle: hasSection125 ? 'MEC Plan' : undefined,
        step: RfcStep.MEC_FORM_ADMIN,
        description: `Configure employee eligibility and administrative settings${hasSection125 ? ' for your MEC plan' : ''}`,
      },
      {
        title: 'Contacts',
        subTitle: hasSection125 ? 'MEC Plan' : undefined,
        step: RfcStep.MEC_FORM_CONTACTS,
        description: `Enter key contact information for your organization${hasSection125 ? ' related to your MEC plan' : ''}`,
      },
      {
        title: 'Payroll',
        subTitle: hasSection125 ? 'MEC Plan' : undefined,
        step: RfcStep.MEC_FORM_PAYROLL_INFO,
        description: `Provide payroll system details${hasSection125 ? ' for your MEC plan' : ''}`,
      },
    );
  }

  if (hasAch) {
    steps.push({
      title: 'ACH Authorization',
      step: RfcStep.ACH_FORM,
      description: `Let's quickly set up your banking details for smooth ACH payments. We'll start with a quick, zero-dollar prenotation test to confirm everything is linked correctly. Don't worry, we'll never debit or credit your account without your direct authorization. This is just the safe, secure on ramp!`,
    });
  }

  if (hasClientPortalAccess) {
    steps.push({
      title: 'Client Portal Access',
      step: RfcStep.CLIENT_PORTAL_ACCESS,
      description: 'Add users who should have access to the client portal',
    });
  }

  steps.push({
    title: 'Review & Submit',
    step: RfcStep.REVIEW,
    description:
      'Attach supporting documents and review all your information before submitting your request for coverage',
  });

  return steps;
}

/** How often to run autosave while the draft form is active. */
const RFC_AUTOSAVE_INTERVAL_MS = 60_000;
/** How long the "saved" banner state stays visible before returning to idle. */
const RFC_AUTOSAVE_STATUS_RESET_SAVED_MS = 3_000;
/** How long the "error" banner state stays visible before returning to idle. */
const RFC_AUTOSAVE_STATUS_RESET_ERROR_MS = 5_000;

export function RfcWizard() {
  return <RfcWizardAccessToken />;
}

function RfcWizardAccessToken() {
  const {accessToken} = useParams<{accessToken: string}>();
  const [currentStep, setCurrentStep] = useState<RfcStep>(RfcStep.INITIAL);
  const [password, setPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [showInitialScreen, setShowInitialScreen] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
  const [confirmedAccuracy, setConfirmedAccuracy] = useState(false);

  // Re-require the accuracy confirmation whenever the user leaves the Review
  // step — edits made on earlier steps invalidate a prior confirmation.
  useEffect(() => {
    if (currentStep !== RfcStep.REVIEW) {
      setConfirmedAccuracy(false);
    }
  }, [currentStep]);

  const form = useForm<WizardFormData>({
    resolver: zodResolver(wizardFormSchema),
    defaultValues: getBlankRfcFormState(),
    mode: 'onChange',
  });

  const rfcQuery = trpc.clients.rfc.getByToken.useQuery(
    {
      accessToken: accessToken!,
      password: password || undefined,
    },
    {
      enabled: !!accessToken,
      retry: false,
      staleTime: STALE_TIME_1_MIN,
      refetchOnWindowFocus: false,
    },
  );

  const updateFormMutation = trpc.clients.rfc.updateForm.useMutation({
    onError: (error) => surfaceRfcFormErrors(error),
  });

  const submitRfcMutation = trpc.clients.rfc.submit.useMutation({
    onError: (error) => surfaceRfcFormErrors(error, 'Submission Error'),
  });

  const persistDraft = useCallback(
    async (params: {
      action: 'SAVE_DRAFT' | 'NEXT_STEP' | 'PREVIOUS_STEP';
      step: RfcStep;
      values?: WizardFormData;
    }) => {
      const values = params.values ?? form.getValues();
      await updateFormMutation.mutateAsync({
        accessToken: accessToken!,
        password: password!,
        action: params.action,
        currentStep: params.step,
        formData: wizardFormValuesToRfcFormData(values),
      });
    },
    [accessToken, password, form, updateFormMutation],
  );

  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>(
    'idle',
  );
  const [draftLastSavedAt, setDraftLastSavedAt] = useState<Date | null>(null);
  const autoSavedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Matches server/last save so we skip no-op interval ticks without relying on `isDirty`. */
  const lastAutosavedSerializedRef = useRef<string | null>(null);

  const scheduleStatusReset = useCallback((ms: number) => {
    if (autoSavedTimeoutRef.current) {
      clearTimeout(autoSavedTimeoutRef.current);
    }
    autoSavedTimeoutRef.current = setTimeout(() => {
      autoSavedTimeoutRef.current = null;
      setAutoSaveStatus('idle');
    }, ms);
  }, []);

  const draftAutosaveIntervalActive =
    !!password &&
    !showInitialScreen &&
    !showPassword &&
    rfcQuery.isSuccess &&
    !!rfcQuery.data &&
    !rfcQuery.data.requiresPassword;

  const performAutoSave = useCallback(async () => {
    if (!accessToken || !password || savingChanges || updateFormMutation.isPending) return;

    const snapshot = JSON.stringify(form.getValues());
    if (snapshot === lastAutosavedSerializedRef.current) return;

    try {
      setAutoSaveStatus('saving');

      await persistDraft({action: 'SAVE_DRAFT', step: currentStep});

      form.reset(form.getValues());
      lastAutosavedSerializedRef.current = JSON.stringify(form.getValues());

      setDraftLastSavedAt(new Date());
      setAutoSaveStatus('saved');
      scheduleStatusReset(RFC_AUTOSAVE_STATUS_RESET_SAVED_MS);
    } catch {
      setAutoSaveStatus('error');
      scheduleStatusReset(RFC_AUTOSAVE_STATUS_RESET_ERROR_MS);
    }
  }, [
    accessToken,
    password,
    savingChanges,
    currentStep,
    form,
    persistDraft,
    scheduleStatusReset,
    updateFormMutation,
  ]);

  useInterval(
    () => {
      void performAutoSave();
    },
    draftAutosaveIntervalActive ? RFC_AUTOSAVE_INTERVAL_MS : null,
  );

  useEffect(() => {
    if (!draftAutosaveIntervalActive && autoSavedTimeoutRef.current) {
      clearTimeout(autoSavedTimeoutRef.current);
      autoSavedTimeoutRef.current = null;
    }
    return () => {
      if (autoSavedTimeoutRef.current) {
        clearTimeout(autoSavedTimeoutRef.current);
      }
    };
  }, [draftAutosaveIntervalActive]);

  const allSteps = getAllStepsForRfc(
    !!rfcQuery.data?.section125FormEnabled,
    !!rfcQuery.data?.mecFormEnabled,
    rfcQuery.data?.clientPortalAccessEnabled ?? true,
    rfcQuery.data?.achAuthorizationEnabled ?? true,
  );
  const currentStepIndex = Math.max(
    allSteps.findIndex((s) => s.step === currentStep),
    0,
  );

  // A draft can have its `currentStep` persisted as a step that's since been
  // disabled for the tenant (e.g. Client Portal Access turned off). When that
  // happens the step is missing from `allSteps`, so the sidebar falls back to
  // index 0 while `RfcStepForm` still renders the orphaned step. Snap to the
  // final (Review) step so the wizard stays consistent and self-heals on save.
  useEffect(() => {
    if (showInitialScreen || currentStep === RfcStep.INITIAL || allSteps.length === 0) return;
    if (!allSteps.some((s) => s.step === currentStep)) {
      setCurrentStep(allSteps[allSteps.length - 1].step);
    }
  }, [allSteps, currentStep, showInitialScreen]);

  useEffect(() => {
    const rfcData = rfcQuery.data;
    if (!rfcData || rfcData.requiresPassword) return;

    form.reset({
      section125Form: rfcData.section125FormData || getBlankRfcFormState().section125Form,
      mecForm: rfcData.mecFormData || getBlankRfcFormState().mecForm,
      clientPortalAccessForm:
        rfcData.clientPortalAccessData || getBlankRfcFormState().clientPortalAccessForm,
    });
    lastAutosavedSerializedRef.current = JSON.stringify(form.getValues());
    if (rfcData.updatedAt) {
      setDraftLastSavedAt(new Date(rfcData.updatedAt));
    }

    const apiStep = rfcData.currentStep as RfcStep | undefined;
    if (!apiStep || apiStep === RfcStep.INITIAL) {
      setShowInitialScreen(true);
      setCurrentStep(RfcStep.INITIAL);
    } else {
      setShowInitialScreen(false);
      setCurrentStep(apiStep);
    }
  }, [rfcQuery.dataUpdatedAt]);

  useEffect(() => {
    if (rfcQuery.data?.requiresPassword) {
      setShowPassword(true);
    }
  }, [rfcQuery.data]);

  useEffect(() => {
    if (rfcQuery.error && password) {
      setPasswordError(rfcQuery.error.message || 'Incorrect password. Please try again.');
      setShowPassword(true);
      setPassword(null);
    }
  }, [rfcQuery.error]);

  const handlePasswordSubmit = (submittedPassword: string) => {
    setPasswordError(undefined);
    setPassword(submittedPassword);
    setShowPassword(false);
  };

  const saveStep = async (action: 'SAVE_DRAFT' | 'NEXT_STEP' | 'PREVIOUS_STEP', step?: RfcStep) => {
    if (savingChanges || !accessToken || !password) return;

    let newStep = step || currentStep;

    if (action === 'PREVIOUS_STEP' && currentStepIndex === 0) {
      return;
    }

    if (action === 'PREVIOUS_STEP' && !step) {
      newStep = allSteps[currentStepIndex - 1].step;
    }

    try {
      setSavingChanges(true);

      await persistDraft({action, step: newStep});

      await rfcQuery.refetch();
    } catch (error) {
      // onError already toasts the field list; map issues to fields for inline highlighting.
      applyRfcFieldErrors(error, form, rfcStepFieldPrefix(newStep));
    }
    setSavingChanges(false);
  };

  const handleFinalSubmit = async () => {
    if (!accessToken || !password) return;

    try {
      await submitRfcMutation.mutateAsync({
        accessToken,
        password,
        formData: wizardFormValuesToRfcFormData(form.getValues()),
      });

      setShowSubmissionSuccess(true);
    } catch (error) {
      // submitRfcMutation.onError already surfaces a toast.
      reportError(error, {silent: true, context: 'RfcWizard.handleFinalSubmit'});
    }
  };

  const handleUseSampleData = () => {
    form.reset(getSampleRfcFormState());
    toast({
      title: 'Sample data loaded',
      description: 'Representative test data has filled every step. Save Draft to persist it.',
    });
  };

  if (showSubmissionSuccess) {
    return <RfcSubmittedScreen />;
  }

  if (showPassword) {
    return (
      <PasswordPrompt
        onSubmit={handlePasswordSubmit}
        clientName={rfcQuery.data?.clientName}
        isLoading={rfcQuery.isFetching}
        error={passwordError}
      />
    );
  }

  if (rfcQuery.isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-pulse'>Loading RFC...</div>
      </div>
    );
  }

  if (rfcQuery.error) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <Alert className='max-w-md'>
          <AlertDescription>{rfcQuery.error.message || 'Failed to load RFC'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const rfcData = rfcQuery.data;
  if (!rfcData) return null;

  const rfcStatus = rfcData.status as RfcStatus;
  const currentStepInfo = allSteps[currentStepIndex];

  if (showInitialScreen) {
    return (
      <RfcInitialScreen
        onGetStarted={() => saveStep('NEXT_STEP')}
        groupName={rfcData.clientName}
        isLoading={savingChanges}
        showAch={rfcData.achAuthorizationEnabled ?? true}
      />
    );
  }

  // Show read-only view if RFC is already submitted
  if (rfcStatus !== RfcStatus.DRAFT) {
    return (
      <div
        className={`min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5 flex items-center justify-center p-6`}
      >
        <Card className='max-w-3xl w-full shadow-xl border-0'>
          <CardContent className='p-12'>
            <div className='space-y-8'>
              <div className='flex items-start justify-between gap-6'>
                <div className='flex items-start gap-6 flex-1'>
                  <div className='relative shrink-0'>
                    <div
                      className={`absolute inset-0 bg-gray-100 rounded-full blur-xl opacity-60 animate-pulse`}
                    />
                    <div
                      className={`relative bg-gradient-to-br from-gray-400 to-gray-600 rounded-full p-5 shadow-lg`}
                    >
                      <FileCheck className='h-12 w-12 text-white' strokeWidth={2.5} />
                    </div>
                  </div>
                  <div className='space-y-3 flex-1'>
                    <h1 className='text-3xl font-bold text-brand-navy'>Request Submitted</h1>
                    {rfcData.clientName && (
                      <div className='flex items-center gap-2'>
                        <Building2 className='h-5 w-5 text-brand-blue' />
                        <p className='text-xl font-semibold text-brand-blue'>
                          {rfcData.clientName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className='bg-white/50 rounded-xl p-6 border border-gray-200'>
                <div className='flex items-start gap-3'>
                  <div className='space-y-2'>
                    <p className='text-sm text-brand-gray-dark'>
                      This request has been submitted and can no longer be edited. If you need to
                      make changes or have questions, please contact our support team.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <RfcWizardLayout
      steps={allSteps}
      currentStepIndex={currentStepIndex}
      currentStep={currentStepInfo}
      onStepClick={(step) => saveStep('PREVIOUS_STEP', step)}
      headerSubtitle={
        rfcData.clientName ? `Complete your coverage request for ${rfcData.clientName}` : undefined
      }
      isRefetching={rfcQuery.isFetching && !rfcQuery.isLoading}
      draftSaveStatus={autoSaveStatus}
      draftLastSavedAt={draftLastSavedAt}
      navButtons={
        <>
          <div className='flex items-center gap-3 flex-wrap'>
            <Button
              type='button'
              variant='outline'
              onClick={() => saveStep('PREVIOUS_STEP')}
              disabled={currentStepIndex === 0 || updateFormMutation.isPending}
              className='bg-white text-brand-navy hover:bg-gray-50 border-gray-200'
            >
              Previous
            </Button>
            {showSampleDataButton && (
              <Button
                type='button'
                variant='ghost'
                onClick={handleUseSampleData}
                disabled={updateFormMutation.isPending}
                className='text-brand-gray-dark hover:text-brand-navy'
              >
                <FlaskConical className='h-4 w-4 mr-2' />
                Use sample data
              </Button>
            )}
          </div>
          {currentStep === RfcStep.REVIEW ? (
            <div className='flex items-center justify-end gap-4 flex-wrap'>
              <label
                htmlFor='rfc-confirm-accuracy'
                className='flex items-center gap-2 text-sm text-brand-navy cursor-pointer'
              >
                <Checkbox
                  id='rfc-confirm-accuracy'
                  checked={confirmedAccuracy}
                  onCheckedChange={(checked) => setConfirmedAccuracy(checked === true)}
                />
                I confirm all information is accurate to the best of my knowledge
              </label>
              <Button
                type='button'
                onClick={() => {
                  handleFinalSubmit();
                }}
                disabled={
                  !confirmedAccuracy || updateFormMutation.isPending || submitRfcMutation.isPending
                }
                className='bg-brand-blue hover:bg-brand-blue-dark text-white shadow-sm'
              >
                Submit
              </Button>
            </div>
          ) : (
            <div className='flex gap-3'>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  saveStep('SAVE_DRAFT');
                }}
                disabled={updateFormMutation.isPending}
                className='bg-brand-gray hover:bg-brand-gray-dark text-white'
              >
                Save Draft
              </Button>
              {currentStepIndex < allSteps.length - 1 && (
                <Button
                  type='button'
                  onClick={() => {
                    saveStep('NEXT_STEP');
                  }}
                  disabled={updateFormMutation.isPending}
                  className='bg-brand-blue hover:bg-brand-blue-dark text-white shadow-sm'
                >
                  Next Step
                  <ArrowRight className='h-4 w-4 ml-2' />
                </Button>
              )}
            </div>
          )}
        </>
      }
    >
      <RfcContext.Provider
        value={{
          accessToken: accessToken!,
          password: password!,
          section125FormEnabled: !!rfcData.section125FormEnabled,
          mecFormEnabled: !!rfcData.mecFormEnabled,
          attachments:
            rfcData.attachments?.map((a) => ({
              id: a.id,
              fileName: a.fileName,
              fileSize: a.fileSize,
              fileType: a.fileType as RfcAttachmentType,
            })) || [],
          refreshAttachments: () => rfcQuery.refetch(),
          saveDraft: performAutoSave,
        }}
      >
        <FormProvider {...form}>
          <RfcStepForm currentStep={currentStep} />
        </FormProvider>
      </RfcContext.Provider>
    </RfcWizardLayout>
  );
}
